/**
 * Users and the `/api/me` payload — master spec §5.1, §2.
 */

import { z } from 'zod';
import { appRoleSchema, tierSchema } from './authz.js';
import { exportPolicyStatusSchema } from './export-control.js';
import { dataEnvelope } from './http.js';

/**
 * The reserved system user (§5.1). Seeds, migrations, every scheduled job, and
 * JIT provisioning open their transaction as this user, because the Phase 2
 * audit trigger raises on a null actor and none of those four has a real
 * person behind it.
 *
 * JIT provisioning is the one that bites if missed: it creates a `users` row
 * for someone who has no id yet, so it cannot supply itself as `app.actor_id`.
 * The symptom otherwise appears on the first sign-in of EVERY new user, during
 * an invite wave, long after this phase looked finished.
 *
 * The id is fixed and obviously synthetic so it is recognizable in audit rows.
 * This is not a §0 "placeholder GUID": that rule is about Entra identifiers
 * that must validate against a real tenant. This is a row this system owns.
 * Kept in sync with the first migration by a test.
 */
export const SYSTEM_USER_ID = '00000000-0000-4000-8000-000000000001';
export const SYSTEM_USER_EMAIL = 'system@menlomicro.internal';

export const EMAIL_REMINDER_MODES = ['off', 'daily', 'weekly'] as const;
export type EmailReminderMode = (typeof EMAIL_REMINDER_MODES)[number];
export const emailReminderModeSchema = z.enum(EMAIL_REMINDER_MODES);

export const userSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  display_name: z.string(),
  /** A cache of the `roles` claim. The token wins on every request (§8d). */
  roles: z.array(z.string()),
  title: z.string().nullable(),
  /**
   * IANA zone, `not null default 'America/Los_Angeles'`. A null timezone would
   * silently exclude that user from every digest forever (§5.1 [R2]).
   */
  timezone: z.string(),
  is_active: z.boolean(),
  email_reminders: emailReminderModeSchema,
  manager_user_id: z.string().uuid().nullable(),
  last_login_at: z.string().nullable(),
});

export type User = z.infer<typeof userSchema>;

/**
 * `/api/me`. UI role checks are driven from this, never from a client-side
 * token decode — one source of truth, and the server's answer is the
 * authoritative one (Phase 1 step 7).
 */
export const meSchema = z.object({
  user: userSchema,
  tier: tierSchema,
  /** The recognized subset of the claim; unknown strings are dropped. */
  roles: z.array(appRoleSchema),
  /** How this request was authenticated. `dev` only ever appears locally. */
  authMode: z.enum(['entra', 'dev']),

  /**
   * The permission matrix RESOLVED for this caller, `resource -> actions`.
   *
   * Phase 4 hides a control the API would refuse by asking this rather than
   * re-deriving the matrix client-side — §2 rule 1 makes the hiding
   * convenience only, but a button that always 403s is still a defect.
   *
   * Optional so an older payload (or a test fixture) still parses; absent
   * means "assume nothing extra is permitted".
   */
  permissions: z.record(z.array(z.string())).optional(),

  /**
   * §8 item 3. `GET /api/me` is one of the middleware's exemptions precisely
   * so the blocking acknowledgment screen can know who it is blocking.
   */
  exportPolicy: exportPolicyStatusSchema.optional(),
});

export type Me = z.infer<typeof meSchema>;
export const meResponseSchema = dataEnvelope(meSchema);
export type MeResponse = z.infer<typeof meResponseSchema>;
